/**
 * Every network read in this app, as TanStack Query hooks.
 *
 * Return types are inferred from the generated schema — none of them is
 * written down here. Change a Rust response struct, regenerate, and any
 * component reading a field that no longer exists stops compiling.
 *
 * There is deliberately no `useEffect`-plus-`fetch` anywhere in this app.
 * Server state is cache state: TanStack Query owns the loading flags, the
 * retries, the deduplication and the invalidation, so components never keep
 * their own stale copy of something the server owns.
 */
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import { CAMPAIGNS_KEY, CAMPAIGNS_STALE, fetchCampaigns } from './campaignList';
import { recordSheetRequest } from './sheetLog';
import { toFilters } from './filterState';
import type { components } from './schema';
import type { paths } from './schema';

/** The vocabulary the engine implements. Effectively static, so cache it hard. */
export function useEnums() {
  return useQuery({
    queryKey: ['enums'],
    queryFn: async () => unwrap(await api.GET('/api/v1/meta/enums', {}), 'EnumsResponse'),
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
  });
}

export function useCampaigns(enabled = true) {
  return useQuery({
    queryKey: CAMPAIGNS_KEY,
    queryFn: fetchCampaigns,
    staleTime: CAMPAIGNS_STALE,
    enabled,
  });
}

/**
 * Create a campaign.
 *
 * The list is cached for five minutes, so it is invalidated here rather than
 * left to go stale: a campaign you just made and cannot see reads as a failure.
 */
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateCampaignBody']) =>
      unwrap(await api.POST('/api/v1/campaigns', { body }), 'CreateCampaignResponse'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export type DashboardPeriod = 'today' | '7d' | '30d' | '90d';

export type DashboardStats = components['schemas']['DashboardStatsResponse'];

/**
 * The dashboard's figures for one period.
 *
 * `keepPreviousData` keeps the last period on screen while the next loads, so
 * switching from 7 to 30 days counts the figures across instead of blanking
 * the page to a skeleton and replaying every entrance. The page dims that
 * stale copy (`isPlaceholderData`) so it is never mistaken for the new one.
 *
 * The minute's refetch is what lets the header say "Live". Structural sharing
 * hands back the same object when nothing changed, so a quiet minute neither
 * re-renders the charts nor replays their animation.
 */
export function useDashboardStats(period: DashboardPeriod) {
  return useQuery({
    queryKey: ['dashboard', period],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/dashboard/stats', { params: { query: { period } } }), 'DashboardStatsResponse'),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Every dimension the leads list can be narrowed by, derived from
 * LeadSearchBody rather than written out here.
 *
 * `Partial` because the Rust struct is `#[serde(default)]` with `Option`
 * fields — everything is genuinely optional — but utoipa emits them as
 * required-and-nullable. Relaxing optionality is safe; the field names and
 * their types still come from the spec, so a renamed field still breaks the
 * build.
 */
export type LeadFilters = Partial<
  NonNullable<
    paths['/api/v1/leads/search']['post']['requestBody']
  >['content']['application/json']
>;

/**
 * POST rather than GET because there are 22 dimensions, several of them
 * arrays. Cramming that into a query string is where filter carry-through
 * bugs come from: a param the client sends, the server drops, and the user
 * sees unfiltered results that look correct.
 */
export function useLeads(filters: LeadFilters, enabled = true) {
  return useQuery({
    queryKey: ['leads', filters],
    enabled,
    queryFn: async () =>
      unwrap(await api.POST('/api/v1/leads/search', { body: filters }), 'LeadListResponse'),
    // Keeps the previous page on screen while the next loads, instead of
    // flashing an empty table on every page change.
    placeholderData: (prev) => prev,
    staleTime: 15 * 1000,
  });
}

/** Fewest characters the command bar searches on: one letter matches nearly every row. */
export const QUICK_SEARCH_MIN = 2;

/**
 * A short list of records matching what was typed in the command bar.
 *
 * The same endpoint as the list page, so a scoped login only ever sees its
 * own scope: the server applies it, and there is no other way in. The
 * caller debounces; this only refuses to run on fewer than two characters.
 */
export function useQuickLeadSearch(term: string, enabled: boolean) {
  const q = term.trim();
  return useQuery({
    queryKey: ['quick-lead-search', q],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/leads/search', { body: { search: q, page: 1, page_size: 8 } }),
        'LeadListResponse',
      ),
    enabled: enabled && q.length >= QUICK_SEARCH_MIN,
    placeholderData: (prev) => prev,
    staleTime: 15 * 1000,
  });
}

/**
 * The command bar's search: free text, filters or both, eight rows at a time.
 *
 * Takes the whole search body, so a typed query such as
 * `status:sold date:today` runs through the same endpoint, and so the same
 * scoping, as the list page. The caller decides when to run (debounced for
 * free text, at once for a filter); this only refuses an empty body.
 */
export function useCommandLeadSearch(body: LeadFilters | null, enabled: boolean) {
  return useQuery({
    queryKey: ['command-lead-search', body],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/leads/search', { body: { ...(body ?? {}), page: 1, page_size: 8 } }),
        'LeadListResponse',
      ),
    enabled: enabled && body !== null,
    placeholderData: (prev) => prev,
    staleTime: 15 * 1000,
  });
}

/**
 * Records by id, for the command bar's "Recently viewed": fetched afresh
 * through the scoped search, so a record this person can no longer see
 * simply is not returned. Only ids are stored locally; names come from here.
 */
export function useLeadsByIds(ids: string[], enabled: boolean) {
  return useQuery({
    queryKey: ['leads-by-ids', ids],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/leads/search', { body: { ids, page: 1, page_size: ids.length } }),
        'LeadListResponse',
      ),
    enabled: enabled && ids.length > 0,
    staleTime: 30 * 1000,
  });
}

/** Whether plain-English search is available to this person. */
export function useSearchInterpretStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['search-interpret-status'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/leads/search/interpret', {}), 'LeadSearchInterpretStatus'),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Turn plain English into the search bar's filters. Returns a query, never records. */
export function useInterpretSearch() {
  return useMutation({
    mutationFn: async (text: string) =>
      unwrap(
        await api.POST('/api/v1/leads/search/interpret', { body: { text } }),
        'LeadSearchInterpretResponse',
      ),
  });
}

/**
 * Custom field names seen on this person's recent records, for autocomplete.
 *
 * Scoped and sampled on the server, and cached for half an hour here: the
 * set of fields changes when an import or a new form adds one, not by the
 * minute. Fetched only once someone starts typing a custom field.
 */
export function useLeadCustomKeys(enabled: boolean) {
  return useQuery({
    queryKey: ['lead-custom-keys'],
    queryFn: async () => unwrap(await api.GET('/api/v1/leads/custom-keys', {}), 'LeadCustomKeysResponse'),
    enabled,
    staleTime: 30 * 60 * 1000,
  });
}

/**
 * The custom fields of some campaigns: the ones each defines on its Fields
 * tab, and the ones seen on its recent leads. For ranking a campaign's own
 * fields first while a search is narrowed to it. Keyed by the campaigns, in
 * a fixed order, so the same set is one cache entry however it was typed.
 */
export function useCampaignCustomKeys(campaignIds: string[], enabled: boolean) {
  const ids = [...new Set(campaignIds)].sort();
  return useQuery({
    queryKey: ['lead-custom-keys', 'campaigns', ids],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/leads/custom-keys', { params: { query: { campaign_ids: ids.join(',') } } }),
        'LeadCustomKeysResponse',
      ),
    enabled: enabled && ids.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/** The source tags and supplier names on this person's records. */
export function useLeadFacets(enabled: boolean) {
  return useQuery({
    queryKey: ['lead-facets'],
    queryFn: async () => unwrap(await api.GET('/api/v1/leads/facets', {}), 'LeadFacetsResponse'),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Saved filter tabs
// ---------------------------------------------------------------------------

export type FilterTabs = NonNullable<
  paths['/api/v1/me/filter-tabs']['get']['responses'][200]['content']['application/json']
>;
export type FilterTab = FilterTabs['tabs'][number];

export function useFilterTabs(enabled = true) {
  return useQuery({
    queryKey: ['filter-tabs'],
    queryFn: async () => unwrap(await api.GET('/api/v1/me/filter-tabs', {}), 'FilterTabsResponse'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Saves the whole tab set. Optimistic: the tab bar updates immediately and
 * rolls back if the server refuses, because renaming a tab should not feel
 * like a network round trip.
 */
export function useSaveFilterTabs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FilterTabs) =>
      unwrap(await api.PUT('/api/v1/me/filter-tabs', { body })),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['filter-tabs'] });
      const previous = qc.getQueryData<FilterTabs>(['filter-tabs']);
      qc.setQueryData(['filter-tabs'], body);
      return { previous };
    },
    onError: (_e, _body, ctx) => {
      if (ctx?.previous) qc.setQueryData(['filter-tabs'], ctx.previous);
    },
  });
}

// ---------------------------------------------------------------------------
// Shared segments
// ---------------------------------------------------------------------------

export type SharedSegment = components['schemas']['SharedSegment'];
export type SharedSegmentList = components['schemas']['SharedSegmentList'];
export type SegmentVisibility = components['schemas']['SegmentVisibility'];
export type SegmentPerson = components['schemas']['SegmentPerson'];

/** The prefix that tells a shared segment's key from a private tab's number. */
export const SHARED_PREFIX = 'shared:';
export const sharedKey = (id: string) => `${SHARED_PREFIX}${id}`;

/**
 * The shared segments this person can see. A server that predates them
 * answers 404, which reads as none rather than as an error on the page.
 */
export function useSharedSegments(enabled = true) {
  return useQuery({
    queryKey: ['shared-segments'],
    queryFn: async (): Promise<SharedSegmentList> => {
      const res = await api.GET('/api/v1/segments/shared', {});
      if (res.response.status === 404) return { segments: [], can_share: false };
      return unwrap(res, 'SharedSegmentList');
    },
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Who a segment can be shared with. Only fetched when the share dialog opens. */
export function useSegmentPeople(enabled: boolean) {
  return useQuery({
    queryKey: ['segment-people'],
    queryFn: async () => unwrap(await api.GET('/api/v1/segments/people', {}), 'SegmentPeople'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Share a segment. With `from_tab_id` the server also takes it out of this
 * person's private tabs, so the cached tabs are refreshed too.
 */
export function useCreateSharedSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateSharedSegmentBody']) =>
      unwrap(await api.POST('/api/v1/segments/shared', { body }), 'SharedSegment'),
    onSuccess: (_seg, body) => {
      // Drop the moved tab from the cached tabs at once, so a save made
      // before the refetch lands cannot write it back as a private copy.
      const moved = body.from_tab_id;
      if (moved != null) {
        qc.setQueryData<FilterTabs>(['filter-tabs'], (old) => {
          if (!old) return old;
          const tabs = old.tabs.filter((t) => t.id !== moved);
          if (tabs.length === 0) return old;
          const active = old.active_tab_id === moved ? (tabs[0]?.id ?? null) : old.active_tab_id;
          return { ...old, tabs, active_tab_id: active };
        });
      }
      void qc.invalidateQueries({ queryKey: ['shared-segments'] });
      void qc.invalidateQueries({ queryKey: ['filter-tabs'] });
    },
  });
}

export function useUpdateSharedSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: components['schemas']['UpdateSharedSegmentBody'] }) =>
      unwrap(
        await api.PUT('/api/v1/segments/shared/{id}', { params: { path: { id } }, body }),
        'SharedSegment',
      ),
    onSuccess: (seg) => {
      // Show the saved version straight away, then confirm with the server.
      qc.setQueryData<SharedSegmentList>(['shared-segments'], (old) =>
        old ? { ...old, segments: old.segments.map((s) => (s.id === seg.id ? seg : s)) } : old,
      );
      void qc.invalidateQueries({ queryKey: ['shared-segments'] });
    },
  });
}

export function useDeleteSharedSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/segments/shared/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shared-segments'] }),
  });
}

/** Stop sharing: the segment goes back into its owner's private segments. */
export function useMakeSegmentPrivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.POST('/api/v1/segments/shared/{id}/make-private', { params: { path: { id } } }),
        'SegmentMadePrivate',
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['shared-segments'] });
      void qc.invalidateQueries({ queryKey: ['filter-tabs'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Personal preferences
// ---------------------------------------------------------------------------

export type UserPreferences = components['schemas']['UserPreferences'];

/** This user's own display preferences. Nothing stored reads as light. */
export function usePreferences() {
  return useQuery({
    queryKey: ['me', 'preferences'],
    queryFn: async () => unwrap(await api.GET('/api/v1/me/preferences', {}), 'UserPreferences'),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * Saves them. Optimistic, so the next read of the cache agrees with what is
 * already on screen rather than flipping the scheme back while the save is in
 * flight.
 */
export function useSavePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UserPreferences) =>
      unwrap(await api.PUT('/api/v1/me/preferences', { body }), 'UserPreferences'),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: ['me', 'preferences'] });
      const previous = qc.getQueryData<UserPreferences>(['me', 'preferences']);
      qc.setQueryData(['me', 'preferences'], body);
      return { previous };
    },
    onError: (_e, _body, ctx) => {
      if (ctx?.previous) qc.setQueryData(['me', 'preferences'], ctx.previous);
    },
  });
}

// ---------------------------------------------------------------------------
// Segment counts
// ---------------------------------------------------------------------------

/** The most segments the server counts in one call. */
export const SEGMENTS_PER_CALL = 25;

export type SegmentCounts = {
  /** Total per tab id. A tab missing here has not been counted yet. */
  totals: Map<number, number>;
  /** Distinct people per tab id, when counted by people. */
  people?: Map<number, number>;
  query_stats: components['schemas']['QueryStats'] | null;
};

/**
 * What the leads list would search for a saved tab, minus paging.
 *
 * The stored `filterState` is opaque to the server and may be in either
 * app's shape, so it is translated here with the same `toFilters` the leads
 * page uses, and sent as-is: no cleaning the list does not also do, so the
 * count and the list's total come from identical bodies.
 */
export function segmentFilters(tab: FilterTab): LeadFilters {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, page_size, ...rest } = toFilters(tab.filter_state);
  return rest;
}

/**
 * A live count for every saved tab, one scan per 25 tabs.
 *
 * Refetches when the window regains focus, since a count is only worth
 * showing while it is roughly current; within 30 seconds it is reused.
 *
 * `unit: 'people'` also counts the distinct people in each, exactly. The
 * server refuses it to a scoped login, so only ask for it for someone who
 * sees people.
 */
export function useSegmentCounts(tabs: FilterTab[] | undefined, unit?: 'people') {
  const segments = (tabs ?? []).map((t) => ({ id: t.id, filter_state: segmentFilters(t) }));
  return useQuery({
    queryKey: unit ? ['segment-counts', segments, unit] : ['segment-counts', segments],
    queryFn: async (): Promise<SegmentCounts> => {
      const chunks: (typeof segments)[] = [];
      for (let i = 0; i < segments.length; i += SEGMENTS_PER_CALL) {
        chunks.push(segments.slice(i, i + SEGMENTS_PER_CALL));
      }
      const results = await Promise.all(
        chunks.map(async (chunk) =>
          unwrap(
            await api.POST('/api/v1/leads/segment-counts', { body: { segments: chunk, ...(unit ? { unit } : {}) } }),
            'SegmentCountsResponse',
          ),
        ),
      );
      const totals = new Map<number, number>();
      const people = new Map<number, number>();
      for (const r of results) {
        for (const c of r.counts) {
          totals.set(c.id, c.total);
          if (c.people != null) people.set(c.id, c.people);
        }
      }
      // One call is the usual case; with several, show the first call's
      // figures rather than invent a combined wall time.
      return { totals, ...(unit ? { people } : {}), query_stats: results[0]?.query_stats ?? null };
    },
    enabled: segments.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Live counts for shared segments, by shared id.
 *
 * The same segment-counts call as private segments, so it runs as whoever is
 * looking, with their own scope: a buyer login counts only its own rows in a
 * segment an admin shared. The endpoint takes numeric ids, so each segment
 * goes up as a negative stand-in that no private tab can have.
 */
export function useSharedSegmentCounts(segments: SharedSegment[] | undefined) {
  const list = segments ?? [];
  const tabs: FilterTab[] = list.map((s, i) => ({
    id: -(i + 1), name: s.name, has_changes: false, filter_state: s.filter_state,
  }));
  const q = useSegmentCounts(tabs);
  const totals = q.data
    ? new Map(list.flatMap((s, i) => {
      const n = q.data.totals.get(-(i + 1));
      return n === undefined ? [] : [[s.id, n] as const];
    }))
    : undefined;
  return { totals, query_stats: q.data?.query_stats ?? null, isPending: q.isPending, error: q.error };
}

/** Detail, delivery attempts and the raw request in one fetch (plan D7). */
export function useLeadDetail(id: string | null) {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/leads/{id}', { params: { path: { id: id as string } } }), 'LeadDetailResponse'),
    enabled: id !== null,
  });
}

/**
 * Return a sold lead.
 *
 * This reverses money. The server zeroes `revenue` and `sold` on the row, so
 * every revenue-derived view is stale afterwards, not just the lead itself —
 * hence the broad invalidation. Getting that wrong is how a UI shows a
 * returned lead still counting toward the day's total.
 */
export function useReturnLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      unwrap(
        await api.POST('/api/v1/leads/{id}/return', {
          params: { path: { id } },
          body: { reason: reason ?? null },
        }),
        'ReturnLeadResponse',
      ),
    onSuccess: (_result, { id }) => {
      qc.invalidateQueries({ queryKey: ['lead', id] });
      for (const key of ['leads', 'dashboard', 'reports', 'entity-stats', 'lead-insights']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

/**
 * Re-queue leads for distribution.
 *
 * Resolves as soon as the leads are queued; the distribution worker runs
 * after, so the outcome is not known yet. The response has no `sold` count for
 * that reason and the UI must not imply one.
 */
export function useRedistribute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      lead_ids: string[];
      skip_caps?: boolean;
      skip_active_days?: boolean;
      skip_filters?: boolean;
      skip_suppression?: boolean;
      skip_buyer_dedupe?: boolean;
    }) => unwrap(await api.POST('/api/v1/leads/redistribute', { body }), 'RedistributeResponse'),
    onSuccess: (_result, body) => {
      for (const id of body.lead_ids) qc.invalidateQueries({ queryKey: ['lead', id] });
      for (const key of ['leads', 'dashboard', 'reports', 'entity-stats']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
  });
}

export type RoutingRange = 'today' | '7d' | '30d' | '90d';

/**
 * Where a campaign's leads actually went, and where they got stuck.
 *
 * Separate from `useCampaignDetail` on purpose: that reads SQLite and returns
 * instantly, this aggregates over ClickHouse. Keeping them apart lets the
 * delivery screen paint its configuration immediately and fill the outcomes in
 * when they arrive.
 */
export function useCampaignRouting(id: string | undefined, dateRange: RoutingRange) {
  return useQuery({
    queryKey: ['campaign-routing', id, dateRange],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/campaigns/{id}/routing', {
          params: { path: { id: id as string }, query: { date_range: dateRange } },
        }),
        'RoutingOutcomesResponse',
      ),
    enabled: Boolean(id),
  });
}

/** Edit a routing group. Invalidates the config and the outcome counts. */
export function useUpdateRouteGroup(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      body,
    }: {
      groupId: string;
      // Taken from the generated schema, never hand-written: rename a field
      // in the Rust struct and this stops compiling.
      body: components['schemas']['UpdateRouteGroupBody'];
    }) =>
      unwrap(
        await api.PUT('/api/v1/campaigns/{id}/groups/{group_id}', {
          params: { path: { id: campaignId as string, group_id: groupId } },
          body,
        }),
        'UpdateRouteGroupResponse',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
      qc.invalidateQueries({ queryKey: ['campaign-routing', campaignId] });
      // What each stream does, and the Routes page, follow the routing.
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['routes'] });
    },
  });
}

/** Edit one destination's link into a group. Link-scoped, not the shared destination. */
export function useUpdateGroupDestination(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      destinationId,
      body,
    }: {
      groupId: string;
      destinationId: string;
      body: components['schemas']['UpdateGroupDestinationBody'];
    }) =>
      unwrap(
        await api.PUT('/api/v1/campaigns/{id}/groups/{group_id}/destinations/{destination_id}', {
          params: {
            path: { id: campaignId as string, group_id: groupId, destination_id: destinationId },
          },
          body,
        }),
        'UpdateGroupDestinationResponse',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
      qc.invalidateQueries({ queryKey: ['campaign-routing', campaignId] });
      // What each stream does, and the Routes page, follow the routing.
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['routes'] });
    },
  });
}

/** Save campaign settings. Merged server-side, so a partial save is safe. */
export function useUpdateCampaign(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['UpdateCampaignBody']) =>
      unwrap(
        await api.PUT('/api/v1/campaigns/{id}', {
          params: { path: { id: campaignId as string } },
          body,
        }),
        'UpdateCampaignResponse',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

/** Replace the custom field definitions. Sends the whole list. */
export function useUpdateCampaignFields(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['UpdateCampaignFieldsBody']) =>
      unwrap(
        await api.PUT('/api/v1/campaigns/{id}/fields', {
          params: { path: { id: campaignId as string } },
          body,
        }),
        'UpdateCampaignResponse',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  });
}

/** Rotate the ingestion key. The old one stops working immediately. */
export function useResetApiKey(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/campaigns/{id}/api-key', {
          params: { path: { id: campaignId as string } },
        }),
        'ResetApiKeyResponse',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaign', campaignId] }),
  });
}

function invalidateRouting(qc: ReturnType<typeof useQueryClient>, campaignId?: string) {
  qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
  qc.invalidateQueries({ queryKey: ['campaign-routing', campaignId] });
  qc.invalidateQueries({ queryKey: ['destinations'] });
  // What each stream does, and the Routes page, follow the routing.
  qc.invalidateQueries({ queryKey: ['campaigns'] });
  qc.invalidateQueries({ queryKey: ['routes'] });
}

/** Create a routing group on a campaign. */
export function useCreateRouteGroup(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateRouteGroupBody']) =>
      unwrap(
        await api.POST('/api/v1/campaigns/{id}/groups', {
          params: { path: { id: campaignId as string } },
          body,
        }),
        'CreateRouteGroupResponse',
      ),
    onSuccess: () => invalidateRouting(qc, campaignId),
  });
}

/** Delete a routing group and its destination links. The destinations survive. */
export function useDeleteRouteGroup(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (groupId: string) => {
      const res = await api.DELETE('/api/v1/campaigns/{id}/groups/{group_id}', {
        params: { path: { id: campaignId as string, group_id: groupId } },
      });
      // 204 carries no body, so unwrap() would read it as an empty response.
      if (res.error !== undefined) throw new Error('Delete failed');
      return groupId;
    },
    onSuccess: () => invalidateRouting(qc, campaignId),
  });
}

/** Link an existing destination into a group. */
export function useLinkDestination(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      body,
    }: {
      groupId: string;
      body: components['schemas']['LinkDestinationBody'];
    }) =>
      unwrap(
        await api.POST('/api/v1/campaigns/{id}/groups/{group_id}/destinations', {
          params: { path: { id: campaignId as string, group_id: groupId } },
          body,
        }),
        'UpdateGroupDestinationResponse',
      ),
    onSuccess: () => invalidateRouting(qc, campaignId),
  });
}

/**
 * Remove a destination from a group.
 *
 * The response says whether the destination row itself was deleted — that
 * happens when this was its last link — so the caller can tell the operator
 * which of the two occurred. One is undone by re-linking; the other is not.
 */
export function useUnlinkDestination(campaignId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, destinationId }: { groupId: string; destinationId: string }) =>
      unwrap(
        await api.DELETE('/api/v1/campaigns/{id}/groups/{group_id}/destinations/{destination_id}', {
          params: {
            path: { id: campaignId as string, group_id: groupId, destination_id: destinationId },
          },
        }),
        'UnlinkDestinationResponse',
      ),
    onSuccess: () => invalidateRouting(qc, campaignId),
  });
}

/** One destination: transport and request shape. Not its per-group terms. */
export function useDestination(id: string | undefined) {
  return useQuery({
    queryKey: ['destination', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/destinations/{id}', { params: { path: { id: id as string } } }),
        'DestinationDetailResponse',
      ),
    enabled: Boolean(id),
  });
}

function invalidateDestination(qc: ReturnType<typeof useQueryClient>, id?: string) {
  qc.invalidateQueries({ queryKey: ['destination', id] });
  qc.invalidateQueries({ queryKey: ['destinations'] });
  // Campaign routing shows each link's endpoint and method, which come from
  // the destination row, so an edit here changes what those screens display.
  qc.invalidateQueries({ queryKey: ['campaign'] });
  qc.invalidateQueries({ queryKey: ['campaign-routing'] });
  qc.invalidateQueries({ queryKey: ['campaigns'] });
  qc.invalidateQueries({ queryKey: ['routes'] });
}

export function useUpdateDestination(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['UpsertDestinationBody']) =>
      unwrap(
        await api.PUT('/api/v1/destinations/{id}', {
          params: { path: { id: id as string } },
          body,
        }),
        'UpsertDestinationResponse',
      ),
    onSuccess: () => invalidateDestination(qc, id),
  });
}

export function useCreateDestination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['UpsertDestinationBody']) =>
      unwrap(await api.POST('/api/v1/destinations', { body }), 'UpsertDestinationResponse'),
    onSuccess: () => invalidateDestination(qc),
  });
}

export function useDeleteDestination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/destinations/{id}', { params: { path: { id } } });
      if (res.error !== undefined) throw new Error('Delete failed');
      return id;
    },
    onSuccess: () => invalidateDestination(qc),
  });
}

/** Fire a real request at an endpoint. A refusal is a result, not an error. */
export function useTestDestination() {
  return useMutation({
    mutationFn: async (body: components['schemas']['TestDestinationBody']) =>
      unwrap(await api.POST('/api/v1/destinations/test', { body }), 'TestDestinationResponse'),
  });
}

/** Judge a sample buyer reply against "accepted when" settings. Sends nothing. */
export function useCheckAcceptance() {
  return useMutation({
    mutationFn: async (body: components['schemas']['AcceptanceCheckBody']) =>
      unwrap(
        await api.POST('/api/v1/destinations/acceptance/check', { body }),
        'AcceptanceCheckResponse',
      ),
  });
}

/** Past verification runs. */
export function useVerifyJobs() {
  return useQuery({
    queryKey: ['verify-jobs'],
    queryFn: async () => unwrap(await api.GET('/api/v1/verify', {}), 'VerifyJobListResponse'),
  });
}

/**
 * One verification, polled while it works.
 *
 * `rows_processed` is what moves during a run — `rows_verified` only settles
 * at the end — so polling stops on a terminal status rather than on counts.
 */
export function useVerifyJob(id: string | null) {
  return useQuery({
    queryKey: ['verify-job', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/verify/{id}', { params: { path: { id: id as string } } }),
        'VerifyJob',
      ),
    enabled: id !== null,
    refetchInterval: (q) => {
      const st = q.state.data?.status;
      return st === 'completed' || st === 'failed' ? false : 1500;
    },
  });
}

export type AuditFilters = {
  action?: string | null;
  resource_type?: string | null;
  http_method?: string | null;
  user_email?: string | null;
  status_class?: string | null;
  q?: string | null;
  before?: string | null;
};

/** Recorded actions, newest first. */
export function useAuditLog(filters: AuditFilters) {
  const query = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v != null && v !== ''),
  );
  return useQuery({
    queryKey: ['audit-log', query],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/audit-log', { params: { query: { limit: 100, ...query } } }),
        'AuditLogListResponse',
      ),
    placeholderData: (prev) => prev,
  });
}

/**
 * Values present in the log, for the filter dropdowns.
 *
 * Served rather than hard-coded, same reason as the enums endpoint: a list of
 * actions written here would offer filters matching nothing and miss ones that
 * exist. Cached long — the set of actions changes only when the server does.
 */
export function useAuditFacets() {
  return useQuery({
    queryKey: ['audit-facets'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/audit-log/facets', {}), 'AuditFacetsResponse'),
    staleTime: 5 * 60 * 1000,
  });
}

export type ReportRange = '7d' | '30d' | '90d' | 'today';

export interface ReportFilters {
  date_range: ReportRange;
  campaign_id?: string;
}

/** Campaign and buyer performance for the selected range. */
export function useReports(filters: ReportFilters) {
  return useQuery({
    queryKey: ['reports', filters],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/reports', { params: { query: filters } }), 'ReportsResponse'),
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export type EntityKind = 'buyers' | 'suppliers';

export function useEntities(kind: EntityKind, enabled = true) {
  return useQuery({
    queryKey: ['entities', kind],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/entities/{kind}', { params: { path: { kind } } }), 'EntityListResponse'),
    staleTime: 60 * 1000,
    enabled,
  });
}

export interface EntityInput {
  name: string;
  email?: string;
  phone?: string;
  active?: boolean;
  settings?: unknown;
}

/**
 * Create or update in one hook. `id` absent means create.
 *
 * Invalidation is the point of routing writes through TanStack Query: the list
 * refetches from the server rather than the component patching its own copy,
 * so the screen cannot drift from what was actually saved.
 */
export function useSaveEntity(kind: EntityKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: string; body: EntityInput }) => {
      if (id) {
        return unwrap(
          await api.PUT('/api/v1/entities/{kind}/{id}', {
            params: { path: { kind, id } },
            body: body as never,
          }),
        );
      }
      return unwrap(
        await api.POST('/api/v1/entities/{kind}', {
          params: { path: { kind } },
          body: body as never,
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities', kind] });
      // Each record is a company's role: a new one may have made the
      // company, and a change shows on the company's page and list.
      qc.invalidateQueries({ queryKey: ['companies'] });
      qc.invalidateQueries({ queryKey: ['company'] });
      qc.invalidateQueries({ queryKey: ['company-roles'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export function useAlerts() {
  return useQuery({
    queryKey: ['alerts'],
    queryFn: async () => unwrap(await api.GET('/api/v1/alerts', {}), 'AlertListResponse'),
    staleTime: 30 * 1000,
  });
}

export function useCreateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateAlertBody']) =>
      unwrap(await api.POST('/api/v1/alerts', { body }), 'AlertWriteResponse'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

export function useUpdateAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: components['schemas']['UpdateAlertBody'];
    }) =>
      unwrap(
        await api.PUT('/api/v1/alerts/{id}', { params: { path: { id } }, body }),
        'AlertWriteResponse',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

export function useDeleteAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.DELETE('/api/v1/alerts/{id}', { params: { path: { id } } });
      if (r.error) throw new Error('Could not delete that alert');
      return true;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

/** Campaign with its routing tiers, links and destinations. */
export function useCampaignDetail(id: string | null) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/campaigns/{id}', {
          params: { path: { id: id as string } },
        }),
        'CampaignDetailResponse',
      ),
    enabled: id !== null,
  });
}

/**
 * Every route of every stream: kind (sell, assign, send), stream, buyer or
 * rep, price and caps as routing resolves them. Read only; the stream's own
 * routing screen edits them.
 */
export function useRoutesOverview(enabled = true) {
  return useQuery({
    queryKey: ['routes'],
    queryFn: async () => unwrap(await api.GET('/api/v1/routes', {}), 'RoutesOverviewResponse'),
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Delivery endpoints, transport only (plan D2). */
export function useDestinations(enabled = true) {
  return useQuery({
    queryKey: ['destinations'],
    queryFn: async () => unwrap(await api.GET('/api/v1/destinations', {}), 'DestinationListResponse'),
    // A rotation's reps are destinations too (delivery method `assign`). They
    // live under Settings › Teams, never in a buyer's list of endpoints. The
    // server leaves them out already; this keeps it so.
    select: (d) => ({ ...d, destinations: d.destinations.filter((x) => x.delivery_method !== 'assign') }),
    staleTime: 60 * 1000,
    enabled,
  });
}

export type InsightsDimension = 'source' | 'hour' | 'day' | 'campaign' | 'buyer' | 'trend';

/**
 * Where the value in the current lead selection comes from.
 *
 * Takes the same filter body as the leads list and runs it through the same
 * WHERE builder server-side, so the modal's totals cannot disagree with the
 * count on the page behind it.
 */
export function useLeadInsights(
  filters: LeadFilters,
  dimension: InsightsDimension,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['lead-insights', dimension, filters],
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/leads/insights', {
          params: { query: { dimension } },
          body: filters,
        }),
        'InsightsResponse',
      ),
    enabled,
    staleTime: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Imports
// ---------------------------------------------------------------------------

/**
 * Import history, newest first.
 *
 * `pageSize` is worth passing when the result is a picker rather than a list:
 * the endpoint defaults to 20 and caps at 100, so a filter built on the
 * default silently cannot see an import older than the last twenty — which
 * looks like the import is missing rather than the page being short.
 */
export function useImports(pageSize?: number) {
  return useQuery({
    queryKey: ['imports', pageSize ?? null],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/imports', pageSize ? { params: { query: { page_size: pageSize } } } : {}),
        'ImportListResponse',
      ),
    staleTime: 15 * 1000,
  });
}

/**
 * Poll a running job. Stops polling once it finishes, rather than hammering
 * the server for a result that will not change.
 */
export function useImportProgress(id: string | null) {
  return useQuery({
    queryKey: ['import', id],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/imports/{id}', { params: { path: { id: id as string } } }),
        'ImportProgress',
      ),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.in_progress ? 1500 : false),
  });
}

export type StartImportBody = NonNullable<
  paths['/api/v1/imports']['post']['requestBody']
>['content']['application/json'];

export function useStartImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: StartImportBody) =>
      unwrap(await api.POST('/api/v1/imports', { body }), 'StartImportResponse'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports'] }),
  });
}

// ---------------------------------------------------------------------------
// Report explorer, saved views, entity stats
// ---------------------------------------------------------------------------

export type ExploreBody = NonNullable<
  paths['/api/v1/reports/explore']['post']['requestBody']
>['content']['application/json'];

export function useExplore(body: ExploreBody, enabled: boolean) {
  return useQuery({
    queryKey: ['explore', body],
    queryFn: async () =>
      unwrap(await api.POST('/api/v1/reports/explore', { body }), 'ExploreResponse'),
    enabled: enabled && body.group_by.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 60 * 1000,
  });
}

export function useEntityStats(params: { date_range?: string; campaign_id?: string }) {
  return useQuery({
    queryKey: ['entity-stats', params],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/reports/entities', { params: { query: params } }),
        'EntityStatsResponse',
      ),
    staleTime: 60 * 1000,
  });
}

export function useReportViews() {
  return useQuery({
    queryKey: ['report-views'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/reports/views', {}), 'ReportViewListResponse'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveReportView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; config: unknown }) =>
      unwrap(await api.POST('/api/v1/reports/views', { body: body as never }), 'ReportView'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-views'] }),
  });
}

export function useDeleteReportView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/reports/views/{id}', { params: { path: { id } } });
      if (res.error !== undefined) throw new Error('Could not delete that view');
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-views'] }),
  });
}

/*
  Sheets: an uploaded import file, viewable as a table.

  Preparation is not instant — a 251MB file takes about five seconds to convert
  and a larger one longer — so `useSheet` polls while the status is `preparing`
  and stops as soon as it settles. `refetchInterval` returning false is what
  stops it; a `setInterval` here would keep firing after the modal closed.
*/
export function useSheet(id: string | null) {
  return useQuery({
    queryKey: ['sheet', id],
    enabled: !!id,
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/sheets/{id}', { params: { path: { id: id! } } }), 'Sheet'),
    refetchInterval: (q) => (q.state.data?.status === 'preparing' ? 1000 : false),
  });
}

/** Every sheet in the account, newest first. */
export function useSheets(enabled = true) {
  return useQuery({
    queryKey: ['sheets'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/sheets', {}), 'SheetListResponse'),
    // Poll while anything is still converting, so the list settles on its own.
    refetchInterval: (q) =>
      q.state.data?.sheets.some((s) => s.status === 'preparing') ? 1500 : false,
  });
}

/**
 * Make a file viewable — either an import already in the system, or a file
 * uploaded straight to the viewer. Calling this for the same import twice
 * returns the same sheet; a failed one is retried.
 */
export function useCreateSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      source:
        | { import_job_id: string }
        | { file_key: string; file_name: string; supplier_id?: string | null },
    ) => unwrap(await api.POST('/api/v1/sheets', { body: source as never }), 'Sheet'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

export function useDeleteSheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/sheets/{id}', { params: { path: { id } } });
      if (res.error !== undefined) throw new Error('Could not delete that sheet');
      return id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

/**
 * One page of a sheet.
 *
 * `placeholderData` keeps the previous page on screen while the next one
 * loads, so paging through a large file does not flash an empty table between
 * every step.
 */
export type SheetFilter = {
  col: string;
  op: string;
  value?: string;
  /** The upper bound for `between`, which includes both ends. */
  value2?: string;
};
export type SheetView = { sort?: string | null; dir?: 'asc' | 'desc'; filters: SheetFilter[] };

/**
 * Everything derived from a sheet's rows.
 *
 * The grid reads `sheet-block`. The paged view it replaced read `sheet-rows`,
 * and when the grid was virtualised the mutations kept invalidating the old
 * key, so thirteen of the fifteen mutations refreshed a query nothing on
 * screen was showing. An edit saved correctly and then disappeared.
 *
 * Naming the keys in one place is the point. Listing them at each call site is
 * what let them drift apart in the first place.
 *
 * Returns a promise that settles when the refetches have. Callers holding an
 * optimistic overlay need to know when the real values have arrived, or they
 * drop it too early and the old value flashes back.
 */
function invalidateSheetRows(qc: QueryClient, sheetId: string | null) {
  return Promise.all(
    ['sheet-block', 'sheet-rows', 'sheet-stats', 'sheet-summary', 'sheet-duplicates', 'sheet-counts']
      .map((key) => qc.invalidateQueries({ queryKey: [key, sheetId] })),
  );
}

/** A view in a few words, for the request log. */
function describeView(filters?: string): string {
  if (!filters) return 'none';
  try {
    return (JSON.parse(filters) as SheetFilter[])
      .map((f) => `${f.col} ${f.op}${f.value ? ` "${f.value}"` : ''}`)
      .join(' and ');
  } catch {
    return filters;
  }
}

/**
 * Run a row request and record what it cost.
 *
 * Wrapped rather than timed at the call sites so every row request is measured
 * the same way, including the ones added later.
 */
async function timedRows<T extends { matching_rows?: number; rows?: unknown[] }>(
  run: () => Promise<T>,
  meta: { offset: number; limit: number; filters?: string; sort?: string | null },
): Promise<T> {
  const started = performance.now();
  try {
    const out = await run();
    recordSheetRequest({
      at: started,
      ms: Math.round(performance.now() - started),
      offset: meta.offset,
      limit: meta.limit,
      filters: describeView(meta.filters),
      sort: meta.sort ?? null,
      matching: out?.matching_rows ?? null,
      returned: out?.rows?.length ?? null,
    });
    return out;
  } catch (e) {
    // A cancelled request is the grid working as intended — the rows went off
    // screen before they arrived — so it is recorded as such rather than as a
    // failure, which would fill the log with alarming lines during a scroll.
    const err = e as Error;
    const cancelled = err.name === 'AbortError' || /abort/i.test(err.message ?? '');
    recordSheetRequest({
      at: started,
      ms: Math.round(performance.now() - started),
      offset: meta.offset,
      limit: meta.limit,
      filters: describeView(meta.filters),
      sort: meta.sort ?? null,
      matching: null,
      returned: null,
      ...(cancelled ? { cancelled: true } : { failed: err.message }),
    });
    throw e;
  }
}

export function useSheetRows(
  id: string | null,
  offset: number,
  limit: number,
  ready: boolean,
  version?: number | null,
  view?: SheetView,
) {
  const filters = view?.filters?.length ? JSON.stringify(view.filters) : undefined;
  return useQuery({
    // Version and view are both part of the key: paging through v2 must not
    // serve pages cached from v3, and a sorted page is not the same page.
    queryKey: ['sheet-rows', id, offset, limit, version ?? null, view?.sort ?? null, view?.dir ?? null, filters ?? null],
    enabled: !!id && ready,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/rows', {
          params: {
            path: { id: id! },
            query: {
              offset,
              limit,
              ...(version ? { version } : {}),
              ...(view?.sort ? { sort: view.sort, dir: view.dir ?? 'asc' } : {}),
              ...(filters ? { filters } : {}),
            },
          },
        }),
        'SheetPage',
      ),
    placeholderData: (prev) => prev,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Save a batch of cell edits.
 *
 * A batch, never one call per keystroke — the server writes them in a single
 * transaction, and one insert per cell measured nearly twice the cost of two
 * hundred together. A `val` of null reverts that cell to what the file says.
 */
export function useSaveSheetEdits(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (edits: { row_no: number; col: string; val: string | null }[]) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/edits', {
          params: { path: { id: sheetId! } },
          body: { edits },
        }),
        'SheetEditSummary',
      ),
    // Only the pages, not the sheet: its row count and columns are unchanged
    // by an edit, and refetching it would restart the `preparing` poll.
    onSuccess: () => invalidateSheetRows(qc, sheetId),
  });
}

/** Throw away every unsaved change — cells and bulk rules — back to the file. */
export function useDiscardSheetEdits(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      unwrap(
        await api.DELETE('/api/v1/sheets/{id}/edits', { params: { path: { id: sheetId! } } }),
        'SheetEditSummary',
      ),
    onSuccess: () => {
      void invalidateSheetRows(qc, sheetId);
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
    },
  });
}

/**
 * Write every change into a new version of the file.
 *
 * The previous version stays in storage, so this moves the sheet forward
 * without destroying what it was.
 */
export function useCommitSheet(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    // The version this was computed from. The server refuses if the file has
    // moved on since, rather than overwriting whatever someone else did.
    // Saving makes a new file with the changes written in; this one is left
    // exactly as it was, so only the list needs refreshing.
    mutationFn: async ({ baseVersion, name, description }: { baseVersion: number } & NewFileDetails) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/commit', {
          params: { path: { id: sheetId! }, query: { base_version: baseVersion } },
          body: { name, description },
        }),
        'Sheet',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

/**
 * Bulk rules on a sheet's columns.
 *
 * A rule, not a set of edited cells: "replace every X with Y" over five
 * million rows is one row on the server and applies in the query that reads
 * the file.
 */
/** One bulk rule on a sheet, as the endpoint returns it. */
export type SheetTransform = components['schemas']['SheetTransform'];

export function useSheetTransforms(sheetId: string | null) {
  return useQuery({
    queryKey: ['sheet-transforms', sheetId],
    enabled: !!sheetId,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/transforms', { params: { path: { id: sheetId! } } }),
        'SheetTransformList',
      ),
  });
}

export function useAddSheetTransform(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: {
      col: string;
      kind: string;
      find?: string;
      replace?: string;
      baseVersion: number;
    }) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/transforms', {
          params: { path: { id: sheetId! }, query: { base_version: t.baseVersion } },
          body: { col: t.col, kind: t.kind, find: t.find, replace: t.replace } as never,
        }),
        'SheetTransformList',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
      void invalidateSheetRows(qc, sheetId);
    },
  });
}

export function useDeleteSheetTransform(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (transformId: string) =>
      unwrap(
        await api.DELETE('/api/v1/sheets/{id}/transforms/{transform_id}', {
          params: { path: { id: sheetId!, transform_id: transformId } },
        }),
        'SheetTransformList',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
      void invalidateSheetRows(qc, sheetId);
    },
  });
}

/**
 * Import a sheet's current contents into a campaign.
 *
 * The server writes the sheet out with edits and rules applied and runs the
 * ordinary import on that, so what lands in the campaign is what the sheet
 * shows rather than what was originally uploaded.
 */
export function useImportSheet(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      campaign_id: string;
      column_mapping: Record<string, string>;
      distribute: boolean;
      /** The view's filters: only the rows on screen are imported. */
      filters: SheetFilter[];
      /** The supplier the whole file came from. */
      supplier_id?: string | null;
    }) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/import', {
          params: { path: { id: sheetId! } },
          body: body as never,
        }),
        'SheetJobStarted',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['imports'] }),
  });
}

/** Check one column of phone numbers against the mobile networks. */
export function useVerifySheetColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (column: string) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/verify', {
          params: { path: { id: sheetId! } },
          body: { column },
        }),
        'SheetJobStarted',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['verify-jobs'] }),
  });
}

/**
 * Everything that has happened to one file.
 *
 * Fetched only when a file is expanded — a list of thirty files should not
 * make thirty requests for histories nobody has asked to see.
 */
export function useSheetHistory(sheetId: string | null) {
  return useQuery({
    queryKey: ['sheet-history', sheetId],
    enabled: !!sheetId,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/history', { params: { path: { id: sheetId! } } }),
        'SheetHistory',
      ),
    // A running import or check moves; stop asking once everything has settled.
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      const busy = [...d.imports, ...d.checks].some(
        (j) => j.status === 'pending' || j.status === 'processing',
      );
      return busy ? 2000 : false;
    },
  });
}

export type SheetCell = { col: string; value: string };

/**
 * How many rows share the values in the selected cells.
 *
 * One request for the whole selection, not one per cell: each is a scan of
 * the columns involved, and the server answers them all in a single pass.
 */
export function useSheetCounts(sheetId: string | null, cells: SheetCell[]) {
  return useQuery({
    queryKey: ['sheet-counts', sheetId, cells],
    enabled: !!sheetId && cells.length > 0,
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/counts', {
          params: { path: { id: sheetId! } },
          body: { cells } as never,
        }),
        'SheetCounts',
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Add a column the file does not have. It starts empty. */
export function useAddSheetColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/columns', {
          params: { path: { id: sheetId! } },
          body: { name },
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
    },
  });
}

/**
 * Rename a column.
 *
 * The stored file is untouched — renaming a column in millions of rows would
 * mean rewriting all of them — so the sheet remembers what the new name reads
 * from until the next save.
 */
export function useRenameSheetColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (r: { from: string; to: string }) =>
      unwrap(
        await api.PATCH('/api/v1/sheets/{id}/columns', {
          params: { path: { id: sheetId! } },
          body: r,
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
    },
  });
}

/** Remove a column, and anything typed into it. */
export function useDropSheetColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) =>
      unwrap(
        await api.DELETE('/api/v1/sheets/{id}/columns', {
          params: { path: { id: sheetId! } },
          body: { name },
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
    },
  });
}

/** Put the columns in a given order. The whole list, never a partial one. */
export function useReorderSheetColumns(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (columns: string[]) =>
      unwrap(
        await api.PUT('/api/v1/sheets/{id}/columns', {
          params: { path: { id: sheetId! } },
          body: { columns },
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
    },
  });
}

/** Values appearing more than once in a column. Asked for, not shown by default. */
export function useSheetDuplicates(sheetId: string | null, col: string | null) {
  return useQuery({
    queryKey: ['sheet-duplicates', sheetId, col],
    enabled: !!sheetId && !!col,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/duplicates', {
          params: { path: { id: sheetId! }, query: { col: col! } },
        }),
        'SheetDuplicates',
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * What each column holds.
 *
 * A scan of every column at once — 1.6s on a 5.6M-row file — so it is fetched
 * when asked for rather than alongside every page.
 */
export function useSheetStats(sheetId: string | null, filters: SheetFilter[], on: boolean) {
  const encoded = filters.length ? JSON.stringify(filters) : undefined;
  return useQuery({
    queryKey: ['sheet-stats', sheetId, encoded ?? null],
    enabled: !!sheetId && on,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/stats', {
          params: { path: { id: sheetId! }, query: encoded ? { filters: encoded } : {} },
        }),
        'SheetStats',
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** The name and description for a file an action makes. Both optional. */
export type NewFileDetails = { name?: string; description?: string };

/**
 * Remove every row matching the filters, or keep only those, as a new file.
 * The file it starts from is left exactly as it was; the answer is the new one.
 */
export function useDeleteSheetRows(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      filters,
      baseVersion,
      keepMatching = false,
      name,
      description,
    }: {
      filters: SheetFilter[];
      baseVersion: number;
      /** Keep the rows the filters match and remove the rest. */
      keepMatching?: boolean;
    } & NewFileDetails) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/delete-rows', {
          params: { path: { id: sheetId! }, query: { base_version: baseVersion } },
          body: { filters, keep_matching: keepMatching, name, description } as never,
        }),
        'Sheet',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

/** Rename a file or change its description. */
export function useUpdateSheet(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { file_name?: string; description?: string; supplier_id?: string }) =>
      unwrap(
        await api.PATCH('/api/v1/sheets/{id}', {
          params: { path: { id: sheetId! } },
          body,
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      qc.invalidateQueries({ queryKey: ['sheets'] });
    },
  });
}

/**
 * Rows fetched in fixed blocks, for a grid that scrolls rather than pages.
 *
 * A block is the unit of caching: scrolling back over rows already seen costs
 * nothing, and scrolling forward fetches only what is newly in view.
 *
 * Measured, not guessed: a block of 250 rows takes about 350ms and a block of
 * 5,000 takes about 330ms. The time goes on reaching object storage and
 * reading the file's index, so rows are very nearly free once a request is
 * being made at all. Bigger blocks therefore cost only bytes, and 1,000 rows
 * is about 210KB — four times fewer requests than 250 for no more waiting.
 * Past a point the payload does start to matter, which is why this is not
 * 5,000.
 *
 * Reading block 20,000 costs what reading block 0 costs, because the server
 * prunes by row number. That is what makes scrolling a five-million-row file
 * feel the same everywhere, and it is why this is worth doing at all.
 */
export const SHEET_BLOCK = 1000;

export function useSheetBlocks(
  id: string | null,
  blocks: number[],
  ready: boolean,
  version?: number | null,
  view?: SheetView,
) {
  const filters = view?.filters?.length ? JSON.stringify(view.filters) : undefined;
  return useQueries({
    queries: blocks.map((block) => ({
      queryKey: [
        'sheet-block', id, block, version ?? null,
        view?.sort ?? null, view?.dir ?? null, filters ?? null,
      ],
      enabled: !!id && ready,
      /*
        The signal is what stops a scroll costing everything it passed over.

        Scrolling through a filtered view asks for the blocks at every position
        it goes through, and without cancelling them they all keep running long
        after their rows are off screen. On a 50M row file that was thirty-two
        requests in flight at once, each one a scan, contending with each other
        until the slowest took thirty seconds — nine and a half minutes of
        query time for one drag of the scrollbar.

        React Query already abandons the results when the blocks change; this
        passes its signal through to `fetch` so the request is abandoned too.
      */
      queryFn: async ({ signal }) =>
        timedRows(
          async () =>
            unwrap(
              await api.GET('/api/v1/sheets/{id}/rows', {
                signal,
                params: {
                  path: { id: id! },
                  query: {
                    offset: block * SHEET_BLOCK,
                    limit: SHEET_BLOCK,
                    ...(version ? { version } : {}),
                    ...(view?.sort ? { sort: view.sort, dir: view.dir ?? 'asc' } : {}),
                    ...(filters ? { filters } : {}),
                  },
                },
              }),
              'SheetPage',
            ),
          {
            offset: block * SHEET_BLOCK,
            limit: SHEET_BLOCK,
            filters,
            sort: view?.sort,
          },
        ),
      staleTime: 5 * 60 * 1000,
    })),
  });
}

/**
 * Remove rows that repeat a value, keeping one of each.
 *
 * Rows with nothing in the chosen column are all kept — they are unknown, not
 * identical.
 */
/**
 * Find the leads whose postcode appears in a column of this file.
 *
 * The result is a new sheet, so this invalidates the sheet LIST rather than
 * the sheet being viewed: nothing about the file you are looking at changed.
 */
export function useMatchLeads(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ col, addressCol }: { col: string; addressCol?: string }) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/match-leads', {
          params: { path: { id: sheetId! } },
          body: { col, address_col: addressCol ?? null },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheets'] });
    },
  });
}

export function useDedupeSheet(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cols,
      keepLast,
      baseVersion,
      name,
      description,
    }: {
      cols: string[];
      keepLast: boolean;
      baseVersion: number;
    } & NewFileDetails) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/dedupe', {
          params: { path: { id: sheetId! }, query: { base_version: baseVersion } },
          body: { cols, keep_last: keepLast, name, description },
        }),
        'Sheet',
      ),
    // A new file: the one it was made from has not changed.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

/**
 * Add a column worked out from other columns.
 *
 * The value is computed as the file is read, so a new column over five million
 * rows costs a row in a table until the sheet is saved.
 */
export function useAddComputedColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      baseVersion,
      ...body
    }: {
      name: string;
      kind: string;
      sources: string[];
      find?: string;
      amount?: string;
      baseVersion: number;
    }) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/computed', {
          params: { path: { id: sheetId! }, query: { base_version: baseVersion } },
          body: body as never,
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
    },
  });
}

/** Fill a column from a written formula, adding it if the file has no such column. */
export function useAddFormulaColumn(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      baseVersion,
      ...body
    }: {
      name: string;
      formula: string;
      baseVersion: number;
    }) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/formula', {
          params: { path: { id: sheetId! }, query: { base_version: baseVersion } },
          body: body as never,
        }),
        'Sheet',
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sheet', sheetId] });
      void invalidateSheetRows(qc, sheetId);
      qc.invalidateQueries({ queryKey: ['sheet-transforms', sheetId] });
    },
  });
}

/**
 * Whether a formula makes sense, without saving it.
 *
 * Debounced by the caller rather than here. The endpoint answers 200 with
 * `ok: false` for a formula half typed, so a failure to compile is a normal
 * answer and not a query error.
 */
export function useCheckFormula(sheetId: string | null, name: string, formula: string) {
  return useQuery({
    queryKey: ['formula-check', sheetId, name, formula],
    enabled: !!sheetId && formula.trim().length > 0,
    queryFn: async () =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/formula/check', {
          params: { path: { id: sheetId! } },
          body: { name, formula } as never,
        }),
        'FormulaCheck',
      ),
    staleTime: 60 * 1000,
  });
}

/** What is in a column and how much of each. Asked for, not shown by default. */
export function useSheetSummary(sheetId: string | null, col: string | null) {
  return useQuery({
    queryKey: ['sheet-summary', sheetId, col],
    enabled: !!sheetId && !!col,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/sheets/{id}/summary', {
          params: { path: { id: sheetId! }, query: { col: col! } },
        }),
        'SheetSummary',
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Remove rows whose value appears in another file. */
export function useSuppressSheet(sheetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      col: string;
      against_sheet_id: string;
      against_col: string;
      address_col?: string | null;
      against_address_col?: string | null;
      keep?: 'missing' | 'matching';
      baseVersion: number;
    } & NewFileDetails) =>
      unwrap(
        await api.POST('/api/v1/sheets/{id}/suppress', {
          params: { path: { id: sheetId! }, query: { base_version: body.baseVersion } },
          body: {
            col: body.col,
            against_sheet_id: body.against_sheet_id,
            against_col: body.against_col,
            address_col: body.address_col ?? null,
            against_address_col: body.against_address_col ?? null,
            keep: body.keep ?? 'missing',
            name: body.name,
            description: body.description,
          },
        }),
        'Sheet',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sheets'] }),
  });
}

/**
 * This organisation's phone-checking settings.
 *
 * Read from this server rather than the gateway, because this is the copy the
 * checker uses. The gateway holds a copy of the same key that nothing here
 * reads. See [[SettingsPage]].
 */
export function useHlrSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'hlr'],
    enabled,
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/settings/hlr', {}), 'HlrSettingsView'),
    staleTime: 60 * 1000,
  });
}

/** Save them. An empty key or secret leaves the stored one alone. */
export function useSaveHlrSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { api_key: string; api_secret: string; test_mode: boolean }) =>
      unwrap(await api.PUT('/api/v1/settings/hlr', { body }), 'HlrSettingsView'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'hlr'] }),
  });
}

/** Apple push credentials, read from this server like the phone-checking ones. */
export function useApnsSettings(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'apns'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/apns', {}), 'ApnsSettingsView'),
    staleTime: 60 * 1000,
  });
}

export function useSaveApnsSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      key_id: string;
      team_id: string;
      p8_key: string;
      bundle_id: string;
    }) => unwrap(await api.PUT('/api/v1/settings/apns', { body }), 'ApnsSettingsView'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'apns'] }),
  });
}

/**
 * Whether anyone in this organisation has answered the one-time sign-up
 * question (`chosen`). Every organisation reads the same words now, so the
 * stored preset changes nothing on screen; the template onboarding reads
 * `chosen` only. Rarely changes, so it is cached hard.
 */
export function useVocabularySetting(enabled: boolean) {
  return useQuery({
    queryKey: ['settings', 'vocabulary'],
    enabled,
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/settings/vocabulary', {}), 'VocabularySettingsView'),
    staleTime: 10 * 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Email templates
// ---------------------------------------------------------------------------

export type EmailTemplate = components['schemas']['EmailTemplate'];
export type UpdateEmailTemplateBody = components['schemas']['UpdateEmailTemplateBody'];

export function useEmailTemplates(enabled = true) {
  return useQuery({
    queryKey: ['email-templates'],
    enabled,
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/email-templates', {}), 'EmailTemplateListResponse'),
  });
}

export function useEmailTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ['email-template', id],
    enabled: !!id,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/email-templates/{id}', { params: { path: { id: id! } } }),
        'EmailTemplate',
      ),
    // The editor owns the document once it is open; refetching under it on
    // focus would reset what someone is typing. Opening it always reads it
    // afresh, though: the cached copy is the one from the last visit, from
    // before that visit's edits were saved.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });
}

export function useCreateEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['CreateEmailTemplateBody']) =>
      unwrap(await api.POST('/api/v1/email-templates', { body }), 'EmailTemplateWriteResponse'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

export function useUpdateEmailTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateEmailTemplateBody) =>
      unwrap(
        await api.PUT('/api/v1/email-templates/{id}', { params: { path: { id } }, body }),
        'EmailTemplateWriteResponse',
      ),
    // The open editor already holds what was saved, so the cached copy is
    // brought up to date rather than refetched under it.
    onSuccess: (_res, body) => {
      qc.setQueryData<EmailTemplate>(['email-template', id], (old) =>
        old ? ({ ...old, ...body } as EmailTemplate) : old,
      );
      return qc.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
}

/** Rename from the list, where there is no editor open to own the template. */
export function useRenameEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) =>
      unwrap(
        await api.PUT('/api/v1/email-templates/{id}', { params: { path: { id } }, body: { name } }),
        'EmailTemplateWriteResponse',
      ),
    onSuccess: (_res, { id, name }) => {
      qc.setQueryData<EmailTemplate>(['email-template', id], (old) => (old ? { ...old, name } : old));
      return qc.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });
}

export function useDeleteEmailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE('/api/v1/email-templates/{id}', { params: { path: { id } } }),
        'EmailTemplateWriteResponse',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  });
}

// ---- Email campaigns, brands, the never-email list and stats ----

export type EmailCampaign = components['schemas']['EmailCampaign'];
export type EmailBrand = components['schemas']['EmailBrand'];
export type EmailBrandBody = components['schemas']['EmailBrandBody'];
export type EmailAudience = components['schemas']['EmailAudience'];
export type EmailStats = components['schemas']['EmailStatsResponse'];
export type EmailStatTotals = components['schemas']['EmailStatTotals'];

export function useEmailStats(range: string) {
  return useQuery({
    queryKey: ['email-stats', range],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/email-stats', { params: { query: { range } } }), 'EmailStatsResponse'),
    refetchInterval: 60_000,
  });
}

export function useEmailCampaigns() {
  return useQuery({
    queryKey: ['email-campaigns'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/email-campaigns', {}), 'EmailCampaignListResponse'),
    refetchInterval: 30_000,
  });
}

export function useEmailBrands() {
  return useQuery({
    queryKey: ['email-brands'],
    queryFn: async () => unwrap(await api.GET('/api/v1/email-brands', {}), 'EmailBrandListResponse'),
  });
}

export function useSaveEmailBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id?: string; body: EmailBrandBody }) =>
      id
        ? unwrap(await api.PUT('/api/v1/email-brands/{id}', { params: { path: { id } }, body }), 'EmailBrand')
        : unwrap(await api.POST('/api/v1/email-brands', { body }), 'EmailBrand'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['email-brands'] });
      void qc.invalidateQueries({ queryKey: ['email-campaigns'] });
    },
  });
}

export function useDeleteEmailBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.DELETE('/api/v1/email-brands/{id}', { params: { path: { id } } }),
        'EmailTemplateWriteResponse',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-brands'] }),
  });
}

/** The never-email list; `reason` narrows the recent rows (`invalid` covers every `invalid:<status>`). */
export function useEmailSuppressions(reason?: string) {
  return useQuery({
    queryKey: ['email-suppressions', reason ?? ''],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/email-suppressions', { params: { query: reason ? { reason } : {} } }),
        'EmailSuppressionListResponse',
      ),
    placeholderData: keepPreviousData,
  });
}

// ---- Email list hygiene: verification, pre-flight, sunset, list health ----

export type ElvSettingsView = components['schemas']['ElvSettingsView'];
export type ElvSettingsBody = components['schemas']['ElvSettingsBody'];
export type ElvTestResponse = components['schemas']['ElvTestResponse'];
export type SunsetSettings = components['schemas']['SunsetSettings'];
export type EmailPreflight = components['schemas']['EmailPreflightResponse'];
export type EmailVerifyJob = components['schemas']['EmailVerifyJobView'];
export type EmailHygiene = components['schemas']['EmailHygieneResponse'];

/** EmailListVerify settings. The key is never sent back, only whether one is saved. */
export function useElvSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings', 'email-verification'],
    enabled,
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/settings/email-verification', {}), 'ElvSettingsView'),
  });
}

export function useSaveElvSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ElvSettingsBody) =>
      unwrap(await api.PUT('/api/v1/settings/email-verification', { body }), 'ElvSettingsView'),
    onSuccess: (data) => {
      qc.setQueryData(['settings', 'email-verification'], data);
      void qc.invalidateQueries({ queryKey: ['email-hygiene'] });
    },
  });
}

/** Ask EmailListVerify for the credit balance, with a typed key or the saved one. */
export function useTestElv() {
  return useMutation({
    mutationFn: async (api_key?: string) =>
      unwrap(
        await api.POST('/api/v1/settings/email-verification/test', { body: { api_key: api_key || null } }),
        'ElvTestResponse',
      ),
  });
}

export function useSunsetSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings', 'email-sunset'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/email-sunset', {}), 'SunsetSettings'),
  });
}

export function useSaveSunsetSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SunsetSettings) =>
      unwrap(await api.PUT('/api/v1/settings/email-sunset', { body }), 'SunsetSettings'),
    onSuccess: (data) => qc.setQueryData(['settings', 'email-sunset'], data),
  });
}

/**
 * How much of what a "send once" schedule would email is verified, and what
 * checking the rest would cost. Spends nothing.
 */
export function useScheduleCheck(workflowId: string | null, scheduleId: string | null) {
  return useQuery({
    queryKey: ['schedule-check', workflowId, scheduleId],
    enabled: !!workflowId && !!scheduleId,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/workflows/{id}/enrol-schedules/{schedule_id}/check', {
          params: { path: { id: workflowId ?? '', schedule_id: scheduleId ?? '' } },
        }),
        'EmailPreflightResponse',
      ),
  });
}

/** Check a "send once" schedule's addresses: EmailListVerify, or the free checks only (send without checking). */
export function useStartScheduleCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ workflowId, scheduleId, mode }: { workflowId: string; scheduleId: string; mode: 'elv' | 'free_only' }) =>
      unwrap(
        await api.POST('/api/v1/workflows/{id}/enrol-schedules/{schedule_id}/check', {
          params: { path: { id: workflowId, schedule_id: scheduleId } },
          body: { mode },
        }),
        'EmailVerifyJobView',
      ),
    onSuccess: (_res, { workflowId }) => qc.invalidateQueries({ queryKey: ['workflow-enrolments', workflowId] }),
  });
}

/** A verification job, polled while it runs. */
export function useEmailVerifyJob(id: string | null | undefined) {
  return useQuery({
    queryKey: ['email-verify-job', id],
    enabled: !!id,
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/email-verify-jobs/{id}', { params: { path: { id: id ?? '' } } }),
        'EmailVerifyJobView',
      ),
    refetchInterval: (q) => (q.state.data?.status === 'running' || !q.state.data ? 3000 : false),
  });
}

/** The overview's list-health panel. */
export function useEmailHygiene() {
  return useQuery({
    queryKey: ['email-hygiene'],
    queryFn: async () => unwrap(await api.GET('/api/v1/email-hygiene', {}), 'EmailHygieneResponse'),
    refetchInterval: 60_000,
  });
}

export function useAddEmailSuppression() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) =>
      unwrap(await api.POST('/api/v1/email-suppressions', { body: { email } }), 'EmailSuppressionCheckResponse'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-suppressions'] }),
  });
}

export function useCheckEmailSuppression() {
  return useMutation({
    mutationFn: async (email: string) =>
      unwrap(
        await api.POST('/api/v1/email-suppressions/check', { body: { email } }),
        'EmailSuppressionCheckResponse',
      ),
  });
}

export function useSesHook(enabled: boolean) {
  return useQuery({
    queryKey: ['ses-hook'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/ses/hook', {}), 'SesHookResponse'),
    staleTime: Infinity,
  });
}

export function useSendTestEmail(id: string) {
  return useMutation({
    mutationFn: async (body: components['schemas']['SendTestEmailBody']) =>
      unwrap(
        await api.POST('/api/v1/email-templates/{id}/test', { params: { path: { id } }, body }),
        'SendTestEmailResponse',
      ),
  });
}

/** Whether the signed-in user can use the assistant, and if not, why. */
export function useAssistantStatus(enabled = true) {
  return useQuery({
    queryKey: ['assistant-status'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/assistant', {}), 'AssistantStatus'),
    staleTime: 60 * 1000,
  });
}

/** Ask the assistant. Send the whole conversation, ending with the question. */
export function useAssistantChat() {
  return useMutation({
    mutationFn: async (body: components['schemas']['AssistantChatBody']) =>
      unwrap(await api.POST('/api/v1/assistant/chat', { body }), 'AssistantChatResponse'),
  });
}

/** Ask the AI to change an email. Returns the changed email; saves nothing. */
export function useAiEditEmail(id: string) {
  return useMutation({
    mutationFn: async (body: components['schemas']['EmailAiEditBody']) =>
      unwrap(
        await api.POST('/api/v1/email-templates/{id}/ai-edit', { params: { path: { id } }, body }),
        'EmailAiEditResponse',
      ),
  });
}

export function useAiSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings', 'ai'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/ai', {}), 'AiSettingsView'),
  });
}

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['AiSettingsBody']) =>
      unwrap(await api.PUT('/api/v1/settings/ai', { body }), 'AiSettingsView'),
    onSuccess: (data) => {
      qc.setQueryData(['settings', 'ai'], data);
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
    },
  });
}

export function useSesSettings(enabled = true) {
  return useQuery({
    queryKey: ['settings', 'ses'],
    enabled,
    queryFn: async () => unwrap(await api.GET('/api/v1/settings/ses', {}), 'SesSettingsView'),
  });
}

export function useSaveSesSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: components['schemas']['SesSettingsBody']) =>
      unwrap(await api.PUT('/api/v1/settings/ses', { body }), 'SesSettingsView'),
    onSuccess: (data) => qc.setQueryData(['settings', 'ses'], data),
  });
}
